import z from 'zod'
import axios from 'axios'
import { SelectedCoinDenom } from '@/wallet-utils'
import { queryKeys } from '@/query-keys'
import { useQuery } from '@tanstack/react-query'
import { fatal } from '@/utils'
import { CHAIN_INFO_LIST, CHAIN_SUPPORTS_LSM } from '@/config'
import { useSelectedChain } from '@/contexts/wallet'
import { useSelectedDenom } from '../../../contexts/denom'

export interface LsmValidator {
  // Validator address
  address: string
  // Amount that the user staked to this specific validator, including amount that is
  // in lock-in period (21 days) due to redelegation
  amount: string
  // When staked tokens are redelegated, the balance is immediately taken away
  // from the origin validator, and immediately added to the destination validator.
  // However, redelegation introduces a lock in period (21 days), which affects all
  // of the staked tokens on the destination validator.
  redelegating: boolean
}

export interface LsmValidatorsQueryPayload {
  validators: Array<LsmValidator>
}

const useLsmValidatorsQuery = () => {
  const [denom] = useSelectedDenom()

  const { address: selectedAddress } = useSelectedChain()

  const queryFn = async (): Promise<LsmValidatorsQueryPayload> => {
    if (!selectedAddress) {
      throw fatal('Unable to query lsm validators while disconnected.')
    }

    const [delegations, redelegations] = await Promise.all([
      // Contains the list of delegations of the address per validator
      getDelegationsPerValidator(denom, selectedAddress),
      // When staked tokens are redelegated, the balance is immediately taken away
      // from the origin validator, and immediately added to the destination validator.
      // Given that redelegation introduces a lock in period (21 days), the tokens on
      // the destination validator are not immediately available for liquid staking.
      getRedelegationRecords(denom, selectedAddress)
    ])

    const validatorMetaData = await Promise.all(
      delegations.map(async (response) => {
        const isRedelegating = Boolean(
          redelegations.find((record) => {
            return record.redelegation.validator_dst_address === response.delegation.validator_address
          })
        )

        return {
          address: response.delegation.validator_address,
          amount: response.balance.amount,
          redelegating: isRedelegating
        }
      })
    )

    return {
      // @FIX https://github.com/Stride-Labs/interface/issues/462
      // We need to filter out validators with 0 amount, as they are not valid for staking.
      // This is probably a bug on the SDK, but whatever.
      validators: validatorMetaData.filter((validator) => {
        return BigInt(validator.amount) > 0
      })
    }
  }

  // Previously, we had an issue with StakeWallet displaying the UI for "Natively staked balance" which
  // is hidden if `lsmValidators == null`. The condition was not working when switching from ATOM (lsm-enabled)
  // to OSMO (lsm-disabled), and causes "natively staked balance" for a while to be displayed when it shouldn't.
  //
  // From the user's perspective, they would see the "natively staked balance" UI for a while, and then it would
  // disappear. The problem was that this query apparently has the value of the previous query key for some reason.
  //
  // @TODO: While this specific case is fixed, we likely will have an issue once we have more LSM-enabled chains.
  return useQuery({
    queryKey: queryKeys.lsmValidatorsByAddress({ address: selectedAddress ?? '' }),
    queryFn,
    enabled: Boolean(selectedAddress && CHAIN_SUPPORTS_LSM[denom])
  })
}

const delegationSchema = z.object({
  delegation: z.object({
    delegator_address: z.string(),
    validator_address: z.string(),
    // "0.000512043537117295"
    shares: z.string()
  }),

  balance: z.object({
    denom: z.string(),
    amount: z.string()
  })
})

const delegationsResponseSchema = z.object({
  delegation_responses: z.array(delegationSchema),
  pagination: z.object({ next_key: z.string().nullable() })
})

// Contains the list of delegations of the address per validator
// Navigates through all the pages if needed
const getDelegationsPerValidator = async (denom: SelectedCoinDenom, address: string) => {
  const instance = axios.create({
    baseURL: CHAIN_INFO_LIST[denom].rest
  })

  const delegations: z.infer<typeof delegationSchema>[] = []

  let pageKey: string | null | undefined = null

  while (true) {
    const params = new URLSearchParams()

    if (pageKey) {
      // For some reason, when we append a null value, we get empty results despite
      // the address actually having some. It seems like this is very specific to this
      // endpoint; transaction history seems to work just fine!
      params.append('pagination.key', `${pageKey}`)
    }

    const response = await instance.get(`cosmos/staking/v1beta1/delegations/${address}?${params}`)

    const { delegation_responses, pagination } = delegationsResponseSchema.parse(response.data)

    for (const delegation of delegation_responses) {
      delegations.push(delegation)
    }

    // There is no next page
    if (!(pageKey = pagination.next_key)) {
      return delegations
    }
  }
}

const redelegationRecordEntrySchema = z.object({
  redelegation_entry: z.object({
    creation_height: z.number(),
    completion_time: z.string(),
    initial_balance: z.string(),
    shares_dst: z.string(),
    unbonding_id: z.number()
  }),
  balance: z.string()
})

const redelegationSchema = z.object({
  redelegation: z.object({
    delegator_address: z.string(),
    validator_src_address: z.string(),
    validator_dst_address: z.string()
  }),
  entries: z.array(redelegationRecordEntrySchema)
})

const redelegationResponseSchema = z.object({
  redelegation_responses: z.array(redelegationSchema)
})

const getRedelegationRecords = async (
  denom: SelectedCoinDenom,
  address: string
): Promise<z.infer<typeof redelegationSchema>[]> => {
  const instance = axios.create({
    baseURL: CHAIN_INFO_LIST[denom].rest
  })

  const response = await instance.get(`cosmos/staking/v1beta1/delegators/${address}/redelegations`)

  return redelegationResponseSchema.parse(response.data).redelegation_responses
}

export { useLsmValidatorsQuery }
